// src/hooks/useSeatsOfRecord.js
//
// WO-001 S3 (2026-09-14, task list section 240). Reads offices/ (by parentOffice) and officeTerms/
// for a committee - both readable by any signed-in account - and hands back the roster projection
// from src\data\seatsOfRecord.js, falling back to the bundle's LC_ROSTER until the seats are seeded.
//
// A10 CLOSED, 2026-09-25 (task list section 411). `offices` was scoped by parentOffice and
// `officeTerms` was NOT - `onSnapshot(collection(db,'officeTerms'))` with no filter at all.
//
// SECTION 412, THE SAME DAY. The deeper sweep found the identical unscoped subscription still
// sitting in src\components\SeatsPanel.js, which section 411 had missed. The shape is now in ONE
// place - src\data\officeTermsQueries.js - and both files read it, rather than the twenty lines
// living twice (SS XI). The reasoning that used to be in this header moved there with the code:
// the committee's own office is not in its own `parentOffice` set, `in` caps at 30, and a partly
// answered set must not be published.
import { useEffect, useMemo, useState } from 'react';
import { db } from '../firebase';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { officeTermIdsFor, subscribeOfficeTerms } from '../data/officeTermsQueries';
import { rosterFromSpine, chooseRoster } from '../data/seatsOfRecord';
import { LC_ROSTER } from '../data/lcRoster';

export default function useSeatsOfRecord(committee) {
  const [offices, setOffices] = useState(null);
  const [terms, setTerms] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!committee) { setOffices([]); return undefined; }
    const u1 = onSnapshot(query(collection(db, 'offices'), where('parentOffice', '==', committee)),
      s => { setOffices(s.docs.map(d => ({ officeId: d.id, ...d.data() }))); setErr(''); },
      e => { setErr('Seats could not be read: ' + (e && e.code ? e.code : e.message)); setOffices([]); });
    return () => u1();
  }, [committee]);

  // Carried as a STRING, not an array. `offices` is a new array on every snapshot, so an array here
  // would be a new identity every time and the terms listener would be torn down and rebuilt on each
  // one - more reads than the unscoped version it replaces. The string only changes when the ids do.
  // null means "offices have not arrived yet", which is not the same as "there are none".
  const idKey = useMemo(
    () => (!committee || offices === null) ? null : JSON.stringify(officeTermIdsFor(committee, offices)),
    [committee, offices]);

  useEffect(() => {
    if (!committee) { setTerms([]); return undefined; }
    if (idKey === null) return undefined;              // offices not in yet - stay loading, never empty
    return subscribeOfficeTerms(db, JSON.parse(idKey), setTerms,
      e => { setErr('Terms could not be read: ' + (e && e.code ? e.code : e.message)); setTerms([]); });
  }, [committee, idKey]);

  const today = new Date().toISOString().slice(0, 10);
  return useMemo(() => {
    const loaded = offices !== null && terms !== null;
    const spine = loaded ? rosterFromSpine(offices, terms, today) : [];
    const chosen = chooseRoster(spine, LC_ROSTER);
    return { roster: chosen.roster, source: loaded ? chosen.source : 'loading', loaded, offices: offices || [], terms: terms || [], error: err };
  }, [offices, terms, today, err]);
}
